import { createContext, useState, useEffect, useContext } from 'react'

export const LanguageContext = createContext()

const translations = {
  en: {
    // Settings
    settings: {
      back: 'Back',
      title: 'API Configuration',
      connectedToServer: 'Connected to server',
      noConnection: 'No connection to server',
      integrationStatus: 'Integration Status',
      of5Active: 'of 11 active APIs',
      configured: 'Configured',
      pending: 'Pending',
      updateKeys: 'Update keys',
      deleteKeys: 'Delete keys',
      deleting: 'Deleting...',
      saveKeys: 'Save keys',
      fbTokenExpired: 'Facebook token expired',
      fbExpiresIn: 'Token expires in {n} days',
      fbRenewUrgent: ' — renew it now in Graph API Explorer',
      fbRenewSoon: ' — renew it soon in Graph API Explorer',
      aiTitle: 'API & integrations',
      aiDesc: 'Read-only key so your AI agent can use this platform\'s MCP server (/mcp) instead of the panel.',
      aiCopyNow: 'Copy it now — it will not be shown again. Add it to your agent with',
      aiCopied: 'Copied', aiCopy: 'Copy',
      aiConfigured: 'Configured', aiNotConfigured: 'Not configured',
      aiRegenerate: 'Generate new (replaces the previous one)', aiGenerate: 'Generate key', aiRevoke: 'Revoke',
      fbTokenSteps: 'To reactivate it: 1) Go to developers.facebook.com/tools/explorer with your Facebook account. 2) Under "Meta App" pick your app (the App ID in this section). 3) Click "Generate Access Token" and accept. 4) Copy the token, paste it in Access Token and save: it is converted automatically into a ~60-day token.',
      saving: 'Saving...',
      saved: 'Saved',
      cancel: 'Cancel',
      howToGet: 'How to get these keys',
      hideInstructions: 'Hide instructions',
      leaveEmpty: "Leave empty the fields you don't want to change",
      enterKeys: 'Enter your keys to activate this service',
      noServerMsg: 'No connection to server',
      services: {
        google_trends: {
          description: 'Real Google search trends by market and period. With RapidAPI configured: reliable real-time data. Without key: uses pytrends (may occasionally be blocked by Google from server IPs).',
          how: [
            'Go to rapidapi.com and log in (or create a free account)',
            'Search for "google trends" — select any provider with a free plan and subscribe',
            'Click Subscribe → select the Basic plan (free)',
            'Go to "Apps" → copy your X-RapidAPI-Key (~50 characters)',
            'Paste the key in the RapidAPI Key field above',
            'In the provider page, open Endpoints — the API Host appears as X-RapidAPI-Host (e.g. google-trends-api.p.rapidapi.com) — copy and paste it here',
          ],
          sharedKeyNote: 'The same RapidAPI key works for all RapidAPI services (Amazon, Pinterest, Google Trends). You can use the same key in each field.',
          freeNote: 'Free plan includes 500 requests/month — sufficient for normal use. Results are cached 1 hour to optimize quota.',
          rateNote: 'Without RapidAPI key, pytrends is used as fallback. Google may temporarily block requests from server IPs (error 429). Configuring RapidAPI eliminates this risk.',
        },
        amazon: {
          description: 'Real prices, product rankings and Amazon market data in real time.',
          how: [
            'Go to rapidapi.com and create a free account',
            'Search "real-time-amazon-data" and subscribe to the Basic/Free plan',
            'In "My Apps" copy your X-RapidAPI-Key (~50 character string)',
          ],
          sharedKeyNote: 'Although Amazon and Pinterest use the same provider (RapidAPI), each requires its own key configured independently.',
        },
        pinterest: {
          description: 'Pinterest Trends: weekly interest chart for any keyword. Requires a Pinterest Trends API that provides time-series interest data (not a pin scraper).',
          how: [
            'Go to rapidapi.com and search Pinterest Trends',
            'Look for an API that provides weekly or monthly interest data (time-series), not just pins',
            'Subscribe and copy your X-RapidAPI-Key',
            'Paste the API Host of that service in the field below',
          ],
          sharedKeyNote: null,
          rateNote: null,
        },
        google_maps: {
          description: 'Real businesses, ratings and local competition in any country.',
          how: [
            'Go to console.cloud.google.com and create a project',
            'In "APIs & Services" → "Library" enable "Places API"',
            'Activate billing (required — $200/month free, ~10,000 calls)',
            'Go to "Credentials" → "Create credentials" → "API Key"',
            'Recommended: restrict the key to "Places API" under "Restrict key"',
          ],
        },
        facebook: {
          name: 'Facebook / Meta Ads Library — Option A: official API',
          description: 'Active competitor ads on Facebook and Instagram. Tier1: US, CA, GB, AU, NZ, ES and all EU.',
          how: [
            'FIRST: verify your identity at business.facebook.com → Authorization & Verifications → "Verify your identity with an organization" (ID/passport — takes 1-48h)',
            'Go to developers.facebook.com → My Apps → Create App → Business type',
            'In Basic Settings copy your App ID and App Secret',
            'Publish the App: Settings → Publish → Live mode',
            'Once your identity is verified, go to facebook.com/ads/library/api → accept terms',
            'Graph API Explorer: select your App → permission ONLY pages_show_list → Generate Access Token',
            'Paste App ID, App Secret and the Access Token here and save: the platform turns it into a ~60-day token automatically',
          ],
          renewNote: 'Official Meta API (free). The token lasts ~60 days, or less if you change your Facebook password. To renew it, generate a new one in Graph API Explorer and update only the "Access Token" field.',
        },
        facebook_rapidapi: {
          name: 'Facebook Ads Library — Option B: RapidAPI',
          description: 'Alternative while Meta has not verified your identity: the same public Ad Library through RapidAPI ("Facebook Ads Library Scraper API"). Used automatically when Option A is not configured or fails.',
          how: [
            'Go to rapidapi.com and search for "Facebook Ads Library Scraper API" (by FetchGoblin)',
            'Pricing tab → Basic plan ($0/month, 50 searches) → Subscribe. Check it says "Hard Limit" (no overage charges)',
            'Copy your X-RapidAPI-Key from the Endpoints tab (code snippet)',
            'Paste it here; the host is already filled in (facebook-ads-library-scraper-api.p.rapidapi.com). Save',
          ],
          renewNote: 'Each new search uses 1 request of your plan (the free plan has 50 per month; one country per search). The same search is kept for 6 hours and does not spend again. Option A (official) is tried first.',
        },
        aliexpress: {
          description: 'Real prices, order counts and product ratings on AliExpress.',
          how: [
            'Go to open.aliexpress.com and create a developer account',
            'Create a new App and request access to the products API',
            'Use case: "Market Research / Price Comparison"',
            'Approval takes 3 to 7 business days',
            'Once approved, copy App Key and App Secret from your App dashboard',
          ],
        },
        google_ads: {
          description: 'Real keyword volumes, CPC and competition from Google Keyword Planner — the most accurate source for search data.',
          how: [
            'Sign in to console.cloud.google.com and create a project',
            'Enable "Google Ads API" in APIs & Services',
            'Create OAuth2 credentials (type: Web application) — copy Client ID and Client Secret',
            'Go to developers.google.com/oauthplayground, authorize "Google Ads API v17", get Refresh Token',
            'Open ads.google.com → Tools → API Center → Apply for developer token (Basic access, takes 1-2 days)',
            'Your Customer ID is the number in the top-right of Google Ads (e.g. 123-456-7890 → enter as 1234567890)',
          ],
          freeNote: 'Free — Google provides this data at no cost with an approved developer token. Basic access: 15,000 operations/month.',
        },
        dataforseo: {
          description: 'Professional keyword research API: real volumes, CPC and competition from Google Ads data. Used by Ahrefs, Moz and other SEO tools internally.',
          how: [
            'Go to dataforseo.com and create a free account',
            'In the dashboard, your Login is your email and your API Password is shown in Account Settings',
            'No approval needed — starts working immediately',
            'Pricing: pay per use, approximately $0.05 per 100 keywords checked',
          ],
          freeNote: 'New accounts receive $1 in free credits (~2,000 keyword checks). No monthly fee.',
          rateNote: 'Pricing: $0.05 per 100 requests. 1 search in this platform = 1 request (6 keywords checked). Very affordable.',
        },
        similarweb: {
          description: 'Real traffic estimates and global/country ranking for any competitor domain (visits, rank, bounce rate). Requires a SimilarWeb-data provider on RapidAPI — field names vary by provider; only fields actually returned are shown, nothing is invented.',
          how: [
            'Go to rapidapi.com and search "SimilarWeb" or "website traffic"',
            'Subscribe to a provider with a free/basic plan',
            'In "Apps" copy your X-RapidAPI-Key',
            'Open that provider\'s Endpoints page and copy its API Host (X-RapidAPI-Host)',
          ],
        },
        semrush: {
          description: 'Domain authority and organic/paid traffic estimates from a SEMrush-data provider on RapidAPI. Same honest rule: only real fields returned by your subscribed provider are displayed.',
          how: [
            'Go to rapidapi.com and search "SEMrush" or "domain authority"',
            'Subscribe to a provider with a free/basic plan',
            'Copy your X-RapidAPI-Key from "Apps"',
            'Copy that provider\'s API Host from its Endpoints page',
          ],
        },
        ahrefs: {
          description: 'Backlinks, referring domains and SEO authority for a competitor domain, via an Ahrefs-data aggregator on RapidAPI (Ahrefs has no official public API). Only real fields returned are displayed.',
          how: [
            'Go to rapidapi.com and search "Ahrefs" or "domain metrics" / "backlink checker"',
            'Subscribe to a provider with a free/basic plan',
            'Copy your X-RapidAPI-Key from "Apps"',
            'Copy that provider\'s API Host from its Endpoints page',
          ],
        },
      },
    },
    // Login
    login: {
      title: 'Market Research Intelligence',
      subtitle: 'Professional Analysis Platform',
      email: 'Email',
      emailPlaceholder: 'admin@empresa.com',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      loginButton: 'Sign In',
      loading: 'Signing in...',
      or: 'OR',
      demoLogin: 'Demo Login',
      demoEmail: 'Demo Email',
      demoPassword: 'Demo Password',
      feature1: 'Product Analysis',
      feature1Desc: 'Analyze any product for viability',
      feature2: 'Multi-language',
      feature2Desc: 'English and Spanish support',
      feature3: 'Professional Reports',
      feature3Desc: 'Download detailed analysis reports'
    },
    // Dashboard
    dashboard: {
      title: 'Dashboard',
      search: 'Search products or services',
      searchPlaceholder: 'e.g. MacBook M2, Dropshipping course...',
      searchPlaceholderService: 'e.g. Email copywriting, Web development...',
      analyse: 'Analyze',
      products: '📦 Products',
      services: '👔 Services',
      analysisType: 'Analysis Type:',
      home: 'Home',
      welcome: 'Welcome',
      logout: 'Logout',
      startAnalysis: 'Start your analysis',
      searchDescription: 'Search for any product or service',
      viabilityScore: 'Viability Score',
      viable: 'VIABLE',
      notViable: 'NOT VIABLE',
      conditional: 'CONDITIONAL',
      recommendation: 'Recommendation',
      demand: 'Demand',
      margin: 'Margin',
      trend: 'Trend',
      audience: 'Audience',
      competition: 'Competition',
      keywords: 'Keywords',
      competitors: 'Competitors',
      niches: 'Unexploited Niches',
      downloadReport: 'Download Report',
      generateReport: 'Generate Report',
      headerSubtitle: 'Intelligent Product & Service Analysis',
      apisButton: 'APIs',
      liveButton: 'Live',
      serverStatus: 'Server Status',
      allOnline: 'All services active',
      someOffline: 'Some services offline',
      checking: 'Checking...',
      refresh: 'Refresh',
      // Welcome dashboard
      apisActive: 'Active APIs',
      configureInSettings: 'Configure in Settings',
      allConnected: 'All connected',
      platforms: 'Platforms',
      dataSources: 'Data sources',
      markets: 'Markets',
      analysisTime: 'Analysis time',
      perSearch: 'Per search',
      platformStatus: 'Platform Status',
      configuredPlatforms: 'Configured platforms',
      active: 'Active',
      pending: 'Pending',
      activePlatforms: 'active platforms',
      configured: 'configured',
      globalTrend: 'Global Trend',
      searchTrendDesc: 'Trend data from your searches',
      noSearchYet: 'No search performed yet',
      trendAfterSearch: 'Search a product to see trend data',
      readyToAnalyze: 'Ready to analyze',
      readyDesc: 'Type any product or service in the search bar. For 100% real data, configure your APIs in',
      settingsPath: 'Settings → APIs',
      activeWithoutConfig: 'Active without configuration',
      // Platform capabilities
      analyticalCapabilities: 'Analytical Capabilities',
      capabilitiesDesc: 'Data sources integrated in each analysis',
      capabilitiesClickDesc: 'Click on a platform to view its metrics',
      viewMetrics: 'View metrics →',
      // Platform capabilities descriptions
      capGoogle: 'Real-time search trends',
      capPinterest: 'Visual trends and content demand',
      capAliexpress: 'Product prices and demand',
      capAmazon: 'BSR, prices and reviews',
      capFacebook: 'Active competitor ads',
      capGoogleAds: 'Keyword volumes and CPC from Google',
      capDataForSeo: 'Professional keyword research data',
      capMaps: 'Local businesses and competition',
      platformSubtitle: 'Professional market intelligence platform — type a product or service to get started',
      chartMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    },
    // Reports
    report: {
      title: 'Analysis Report',
      generatedOn: 'Generated on',
      analysisOf: 'Analysis of',
      viabilityAssessment: 'Viability Assessment',
      recommendation: 'Recommendation',
      proceed: 'Proceed immediately',
      analyze: 'Analyze with caution',
      notRecommended: 'Not recommended',
      details: 'Details',
      confidence: 'Confidence Level',
      marketAnalysis: 'Market Analysis',
      competitiveIntelligence: 'Competitive Intelligence',
      downloadPDF: 'Download as PDF',
      close: 'Close'
    }
  },
  es: {
    // Settings
    settings: {
      back: 'Volver',
      title: 'Configuracion de APIs',
      connectedToServer: 'Conectado al servidor',
      noConnection: 'Sin conexion con el servidor',
      integrationStatus: 'Estado de integraciones',
      of5Active: 'de 11 APIs activas',
      configured: 'Configurado',
      pending: 'Pendiente',
      updateKeys: 'Actualizar claves',
      deleteKeys: 'Borrar claves',
      deleting: 'Borrando...',
      saveKeys: 'Guardar claves',
      fbTokenExpired: 'Token de Facebook vencido',
      fbExpiresIn: 'El token vence en {n} días',
      fbRenewUrgent: ' — renuévalo ya en Graph API Explorer',
      fbRenewSoon: ' — renuévalo pronto en Graph API Explorer',
      aiTitle: 'API e integraciones',
      aiDesc: 'Clave de solo lectura para que tu agente IA use el servidor MCP de esta plataforma (/mcp) en vez del panel.',
      aiCopyNow: 'Cópiala ahora — no se vuelve a mostrar. Ponla en tu agente con',
      aiCopied: 'Copiada', aiCopy: 'Copiar',
      aiConfigured: 'Configurada', aiNotConfigured: 'Sin configurar',
      aiRegenerate: 'Generar nueva (reemplaza la anterior)', aiGenerate: 'Generar clave', aiRevoke: 'Revocar',
      fbTokenSteps: 'Para activarlo: 1) Entra a developers.facebook.com/tools/explorer con tu cuenta de Facebook. 2) En "Meta App" elige tu app (la del App ID de esta sección). 3) Pulsa "Generate Access Token" y acepta. 4) Copia el token, pégalo en Access Token y guarda: se convierte solo en uno de ~60 días.',
      saving: 'Guardando...',
      saved: 'Guardado',
      cancel: 'Cancelar',
      howToGet: 'Como obtener estas claves',
      hideInstructions: 'Ocultar instrucciones',
      leaveEmpty: 'Deja vacio los campos que no quieras cambiar',
      enterKeys: 'Introduce tus claves para activar este servicio',
      noServerMsg: 'Sin conexion con el servidor',
      services: {
        google_trends: {
          description: 'Tendencias de busqueda reales de Google por mercado y periodo. Con RapidAPI configurada: datos reales sin bloqueos. Sin clave: usa pytrends (puede ser bloqueado ocasionalmente por Google desde IPs de servidor).',
          how: [
            'Ve a rapidapi.com e inicia sesion (o crea una cuenta gratuita)',
            'Busca "google trends" — selecciona cualquier proveedor con plan gratuito y suscribete',
            'Haz clic en Subscribe → selecciona el plan Basic (gratuito)',
            'Ve a "Apps" → copia tu X-RapidAPI-Key (cadena de ~50 caracteres)',
            'Pega la clave en el campo RapidAPI Key de arriba',
            'En la pagina del proveedor, abre Endpoints — el API Host aparece como X-RapidAPI-Host (ej. google-trends-api.p.rapidapi.com) — copialo y pegalo aqui',
          ],
          sharedKeyNote: 'La misma clave de RapidAPI funciona para todos los servicios RapidAPI (Amazon, Pinterest, Google Trends). Puedes usar la misma clave en cada campo.',
          freeNote: 'El plan gratuito incluye 500 peticiones/mes — suficiente para uso normal. Los resultados se almacenan en cache 1 hora para optimizar la cuota.',
          rateNote: 'Sin clave RapidAPI se usa pytrends como alternativa. Google puede bloquear temporalmente peticiones desde IPs de servidor (error 429). Configurar RapidAPI elimina este riesgo.',
        },
        amazon: {
          description: 'Precios reales, ranking de productos y datos de mercado de Amazon en tiempo real.',
          how: [
            'Ve a rapidapi.com y crea una cuenta gratuita',
            'Busca "real-time-amazon-data" y suscribete al plan Basic/Free',
            'En "My Apps" copia tu X-RapidAPI-Key (cadena de ~50 caracteres)',
          ],
          sharedKeyNote: 'Aunque Amazon y Pinterest usan el mismo proveedor (RapidAPI), cada uno requiere su propia clave configurada de forma independiente.',
        },
        pinterest: {
          description: 'Pinterest Trends: grafico de interes semanal para cualquier keyword. Requiere una API de Pinterest Trends que provea datos historicos en serie de tiempo (no un scraper de pins).',
          how: [
            'Ve a rapidapi.com y busca Pinterest Trends',
            'Busca una API que provea datos de interes semanal o mensual (serie de tiempo), no solo pins',
            'Suscribete y copia tu X-RapidAPI-Key',
            'Pega el API Host de ese servicio en el campo de abajo',
          ],
          sharedKeyNote: null,
          rateNote: null,
        },
        google_maps: {
          description: 'Negocios reales, ratings y competencia local en cualquier pais.',
          how: [
            'Ve a console.cloud.google.com y crea un proyecto',
            'En "APIs y servicios" → "Biblioteca" habilita "Places API"',
            'Activa la facturacion (obligatorio — $200/mes gratis, ~10.000 llamadas)',
            'Ve a "Credenciales" → "Crear credenciales" → "Clave de API"',
            'Recomendado: restringe la clave a "Places API" en "Restringir clave"',
          ],
        },
        facebook: {
          name: 'Facebook / Meta Ads Library — Opción A: API oficial',
          description: 'Anuncios activos de competidores en Facebook e Instagram. Tier1: US, CA, GB, AU, NZ, ES y toda la UE.',
          how: [
            'PRIMERO: verifica tu identidad en business.facebook.com → Autorizaciones y verificaciones → "Verifica tu identidad a una organizacion" (DNI/pasaporte — tarda 1-48h)',
            'Ve a developers.facebook.com → Mis aplicaciones → Crear aplicacion → tipo Empresa',
            'En Configuracion basica copia tu App ID y App Secret',
            'Publica la App: Configuracion → Publicar → modo Live',
            'Una vez verificada tu identidad, ve a facebook.com/ads/library/api → acepta los terminos',
            'Graph API Explorer: selecciona tu App → permiso SOLO pages_show_list → Generate Access Token',
            'Pega aquí App ID, App Secret y el Access Token y guarda: la plataforma lo convierte sola en uno de ~60 días',
          ],
          renewNote: 'API oficial de Meta (gratis). El token dura ~60 días, o menos si cambias la contraseña de Facebook. Para renovarlo, genera uno nuevo en Graph API Explorer y actualiza solo el campo "Access Token".',
        },
        facebook_rapidapi: {
          name: 'Facebook Ads Library — Opción B: RapidAPI',
          description: 'Alternativa mientras Meta no verifica tu identidad: la misma Biblioteca de Anuncios pública a través de RapidAPI ("Facebook Ads Library Scraper API"). Se usa sola cuando la Opción A no está configurada o falla.',
          how: [
            'Entra a rapidapi.com y busca "Facebook Ads Library Scraper API" (de FetchGoblin)',
            'Pestaña Pricing → plan Basic ($0/mes, 50 búsquedas) → Subscribe. Confirma que diga "Hard Limit" (sin cobros por exceso)',
            'Copia tu X-RapidAPI-Key desde la pestaña Endpoints (bloque de código)',
            'Pégala aquí; el host ya viene puesto (facebook-ads-library-scraper-api.p.rapidapi.com). Guarda',
          ],
          renewNote: 'Cada búsqueda nueva usa 1 consulta de tu plan (el gratuito trae 50 al mes; un país por búsqueda). La misma búsqueda se guarda 6 horas y no vuelve a gastar. Primero se intenta la Opción A (oficial).',
        },
        aliexpress: {
          description: 'Precios reales, conteo de ordenes y ratings de productos en AliExpress.',
          how: [
            'Ve a open.aliexpress.com y crea una cuenta de desarrollador',
            'Crea una nueva App y solicita acceso a la API de productos',
            'Caso de uso: "Market Research / Price Comparison"',
            'La aprobacion tarda entre 3 y 7 dias habiles',
            'Una vez aprobada, en el panel de tu App copia App Key y App Secret',
          ],
        },
        google_ads: {
          description: 'Volumenes reales de busqueda, CPC y competencia de Google Keyword Planner — la fuente mas precisa para datos de busqueda.',
          how: [
            'Accede a console.cloud.google.com y crea un proyecto',
            'Activa "Google Ads API" en APIs y Servicios',
            'Crea credenciales OAuth2 (tipo: Aplicacion web) — copia Client ID y Client Secret',
            'Ve a developers.google.com/oauthplayground, autoriza "Google Ads API v17", obtiene Refresh Token',
            'Abre ads.google.com → Herramientas → Centro de API → Solicita developer token (acceso Basic, tarda 1-2 dias)',
            'Tu Customer ID es el numero arriba a la derecha en Google Ads (ej. 123-456-7890 → pon 1234567890)',
          ],
          freeNote: 'Gratuito — Google proporciona estos datos sin coste con developer token aprobado. Acceso Basic: 15.000 operaciones/mes.',
        },
        dataforseo: {
          description: 'API profesional de investigacion de keywords: volumenes reales, CPC y competencia de datos de Google Ads. Usada internamente por Ahrefs, Moz y otras tools SEO.',
          how: [
            'Ve a dataforseo.com y crea una cuenta gratuita',
            'En el panel, tu Login es tu email y tu API Password aparece en Configuracion de cuenta',
            'No necesita aprobacion — funciona inmediatamente',
            'Precio: pago por uso, aproximadamente $0.05 por 100 keywords consultadas',
          ],
          freeNote: 'Las cuentas nuevas reciben $1 en creditos gratuitos (~2.000 consultas de keywords). Sin mensualidad.',
          rateNote: 'Precio: $0.05 por 100 peticiones. 1 busqueda en esta plataforma = 1 peticion (6 keywords). Muy economico.',
        },
        similarweb: {
          description: 'Trafico estimado y ranking global/por pais de cualquier dominio competidor (visitas, rank, tasa de rebote). Requiere un proveedor de datos SimilarWeb en RapidAPI — los nombres de campo varian segun el proveedor; solo se muestran los campos que la API realmente devuelve, nada se inventa.',
          how: [
            'Ve a rapidapi.com y busca "SimilarWeb" o "website traffic"',
            'Suscribete a un proveedor con plan gratuito/basico',
            'En "Apps" copia tu X-RapidAPI-Key',
            'Abre la pagina de Endpoints de ese proveedor y copia su API Host (X-RapidAPI-Host)',
          ],
        },
        semrush: {
          description: 'Autoridad de dominio y estimaciones de trafico organico/pago de un proveedor de datos SEMrush en RapidAPI. Misma regla de honestidad: solo se muestran los campos reales que devuelva tu proveedor contratado.',
          how: [
            'Ve a rapidapi.com y busca "SEMrush" o "domain authority"',
            'Suscribete a un proveedor con plan gratuito/basico',
            'Copia tu X-RapidAPI-Key desde "Apps"',
            'Copia el API Host de ese proveedor desde su pagina de Endpoints',
          ],
        },
        ahrefs: {
          description: 'Backlinks, dominios de referencia y autoridad SEO de un dominio competidor, via un agregador de datos Ahrefs en RapidAPI (Ahrefs no tiene API publica oficial). Solo se muestran los campos reales que la API devuelva.',
          how: [
            'Ve a rapidapi.com y busca "Ahrefs" o "domain metrics" / "backlink checker"',
            'Suscribete a un proveedor con plan gratuito/basico',
            'Copia tu X-RapidAPI-Key desde "Apps"',
            'Copia el API Host de ese proveedor desde su pagina de Endpoints',
          ],
        },
      },
    },
    // Login
    login: {
      title: 'Market Research Intelligence',
      subtitle: 'Plataforma de Análisis Profesional',
      email: 'Correo Electrónico',
      emailPlaceholder: 'admin@empresa.com',
      password: 'Contraseña',
      passwordPlaceholder: 'Ingresa tu contraseña',
      loginButton: 'Iniciar Sesión',
      loading: 'Iniciando sesión...',
      or: 'O',
      demoLogin: 'Acceso Demo',
      demoEmail: 'Email Demo',
      demoPassword: 'Contraseña Demo',
      feature1: 'Análisis de Productos',
      feature1Desc: 'Analiza cualquier producto por viabilidad',
      feature2: 'Multiidioma',
      feature2Desc: 'Soporte en inglés y español',
      feature3: 'Reportes Profesionales',
      feature3Desc: 'Descarga reportes de análisis detallados'
    },
    // Dashboard
    dashboard: {
      title: 'Panel de Control',
      search: 'Busca productos o servicios',
      searchPlaceholder: 'Ej: MacBook M2, Dropshipping course...',
      searchPlaceholderService: 'Ej: Email copywriting, Web development...',
      analyse: 'Analizar',
      products: '📦 Productos',
      services: '👔 Servicios',
      analysisType: 'Tipo de Análisis:',
      home: 'Inicio',
      welcome: 'Bienvenido',
      logout: 'Cerrar Sesión',
      startAnalysis: 'Comienza tu análisis',
      searchDescription: 'Busca cualquier producto o servicio',
      viabilityScore: 'Puntuación de Viabilidad',
      viable: 'VIABLE',
      notViable: 'NO VIABLE',
      conditional: 'CONDICIONAL',
      recommendation: 'Recomendación',
      demand: 'Demanda',
      margin: 'Margen',
      trend: 'Tendencia',
      audience: 'Audiencia',
      competition: 'Competencia',
      keywords: 'Palabras Clave',
      competitors: 'Competidores',
      niches: 'Nichos Sin Explotar',
      downloadReport: 'Descargar Reporte',
      generateReport: 'Generar Reporte',
      headerSubtitle: 'Análisis Inteligente de Productos & Servicios',
      apisButton: 'APIs',
      liveButton: 'En Vivo',
      serverStatus: 'Estado de Servidores',
      allOnline: 'Todos los servicios activos',
      someOffline: 'Algunos servicios sin conexión',
      checking: 'Verificando...',
      refresh: 'Refrescar',
      // Welcome dashboard
      apisActive: 'APIs Activas',
      configureInSettings: 'Configura en Ajustes',
      allConnected: 'Todas conectadas',
      platforms: 'Plataformas',
      dataSources: 'Fuentes de datos',
      markets: 'Mercados',
      analysisTime: 'Tiempo de análisis',
      perSearch: 'Por búsqueda',
      platformStatus: 'Estado de Plataformas',
      configuredPlatforms: 'Plataformas configuradas',
      active: 'Activo',
      pending: 'Pendiente',
      activePlatforms: 'plataformas activas',
      configured: 'configurado',
      globalTrend: 'Tendencia Global',
      searchTrendDesc: 'Datos de tendencia de tus búsquedas',
      noSearchYet: 'Aún no se ha realizado una búsqueda',
      trendAfterSearch: 'Busca un producto para ver la tendencia',
      readyToAnalyze: 'Listo para analizar',
      readyDesc: 'Escribe cualquier producto o servicio en la barra de búsqueda. Para datos 100% reales, configura tus APIs en',
      settingsPath: 'Ajustes → APIs',
      activeWithoutConfig: 'Activo sin configuración',
      // Platform capabilities
      analyticalCapabilities: 'Capacidades Analíticas',
      capabilitiesDesc: 'Fuentes de datos integradas en cada análisis',
      capabilitiesClickDesc: 'Haz clic en una plataforma para ver sus métricas',
      viewMetrics: 'Ver métricas →',
      // Platform capabilities descriptions
      capGoogle: 'Tendencias de búsqueda en tiempo real',
      capPinterest: 'Tendencias visuales y demanda de contenido',
      capAliexpress: 'Precios y demanda de productos',
      capAmazon: 'BSR, precios y reseñas',
      capFacebook: 'Anuncios de competidores activos',
      capGoogleAds: 'Volumenes de keywords y CPC de Google',
      capDataForSeo: 'Investigacion profesional de keywords',
      capMaps: 'Negocios locales y competencia',
      platformSubtitle: 'Plataforma profesional de inteligencia de mercado — escribe un producto o servicio para comenzar',
      chartMonths: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    },
    // Reports
    report: {
      title: 'Reporte de Análisis',
      generatedOn: 'Generado el',
      analysisOf: 'Análisis de',
      viabilityAssessment: 'Evaluación de Viabilidad',
      recommendation: 'Recomendación',
      proceed: 'Proceder inmediatamente',
      analyze: 'Analizar con cautela',
      notRecommended: 'No recomendado',
      details: 'Detalles',
      confidence: 'Nivel de Confianza',
      marketAnalysis: 'Análisis de Mercado',
      competitiveIntelligence: 'Inteligencia Competitiva',
      downloadPDF: 'Descargar en PDF',
      close: 'Cerrar'
    }
  }
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es')

  // Cargar idioma guardado
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'es'
    setLanguage(savedLanguage)
  }, [])

  const switchLanguage = (lang) => {
    setLanguage(lang)
    localStorage.setItem('language', lang)
  }

  const t = (key) => {
    const keys = key.split('.')
    let value = translations[language]
    
    for (const k of keys) {
      value = value?.[k]
    }
    
    return value || key
  }

  return (
    <LanguageContext.Provider value={{ language, switchLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider')
  }
  return context
}
